<template>
  <div class="consumer-rights-form">
    <div class="form-body">
      <div class="form-label">
        <span class="label-text">联系方式</span>
        <span class="label-required">（*必填）</span>
      </div>
      <div class="form-input-wrap">
        <input
          v-model="form.phone"
          class="form-input"
          type="tel"
          maxlength="11"
          placeholder="请填写常用联系手机号，专员将通过该号码与您沟通"
          @input="onFormChange"
        />
      </div>

      <div class="form-label form-label-single">
        <span class="label-text">订单号</span>
      </div>
      <div class="form-input-wrap">
        <input
          v-model="form.orderNo"
          class="form-input"
          type="text"
          placeholder="请输入订单号"
          @input="onFormChange"
        />
      </div>

      <div class="form-label">
        <span class="label-text">问题描述</span>
        <span class="label-required">（*必填）</span>
      </div>
      <div class="form-textarea-wrap">
        <textarea
          v-model="form.description"
          class="form-textarea"
          maxlength="200"
          placeholder="请输入问题描述"
          @input="onFormChange"
        />
        <span class="char-count">{{ descriptionLen }}/200</span>
      </div>

      <div class="form-label">
        <span class="label-text">您的诉求</span>
        <span class="label-required">（*必填）</span>
      </div>
      <div class="form-textarea-wrap">
        <textarea
          v-model="form.demand"
          class="form-textarea"
          maxlength="200"
          placeholder="请输入您的诉求"
          @input="onFormChange"
        />
        <span class="char-count">{{ demandLen }}/200</span>
      </div>

      <button class="submit-btn" @click="handleSubmit">提交反馈</button>
    </div>

    <ExitConfirmDialog
      :visible="showExitConfirm"
      @abandon="onAbandon"
      @continue="onContinue"
    />
  </div>
</template>

<script>
import { consumerRightsComplaint } from "@/apis";
import { alert } from "@/utils/confirm";
import ExitConfirmDialog from "./components/ExitConfirmDialog.vue";
import {
  clearFormCache,
  getFormCache,
  saveFormCache,
  validatePhone,
} from "./utils";

export default {
  name: "consumerRightsForm",
  components: {
    ExitConfirmDialog,
  },
  data() {
    return {
      form: {
        phone: "",
        orderNo: "",
        description: "",
        demand: "",
      },
      showExitConfirm: false,
      pendingNavigation: null,
      leaveCallback: null,
      isSubmitting: false,
    };
  },
  computed: {
    descriptionLen() {
      return this.form.description.length;
    },
    demandLen() {
      return this.form.demand.length;
    },
    hasFormData() {
      const { phone, orderNo, description, demand } = this.form;
      return !!(phone || orderNo || description || demand);
    },
  },
  mounted() {
    this.restoreCache();
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSubmitting || to.name === "consumerRightsSuccess") {
      next();
      return;
    }
    if (this.hasFormData && !this.showExitConfirm) {
      this.pendingNavigation = next;
      this.showExitConfirm = true;
      next(false);
      return;
    }
    next();
  },
  methods: {
    restoreCache() {
      const cache = getFormCache();
      if (cache) {
        this.form = {
          phone: cache.phone || "",
          orderNo: cache.orderNo || "",
          description: cache.description || "",
          demand: cache.demand || "",
        };
      }
    },
    onFormChange() {
      saveFormCache({ ...this.form });
    },
    validateForm() {
      const phoneError = validatePhone(this.form.phone);
      if (phoneError) {
        alert({ message: phoneError });
        return false;
      }
      if (!this.form.description.trim()) {
        alert({ message: "请填写问题描述" });
        return false;
      }
      if (!this.form.demand.trim()) {
        alert({ message: "请填写您的诉求" });
        return false;
      }
      return true;
    },
    async handleSubmit() {
      if (!this.validateForm()) return;

      const loading = this.$loading();
      this.isSubmitting = true;
      try {
        const res = await consumerRightsComplaint({
          risk_phone_number: this.form.phone,
          phone_number: this.form.phone,
          order_number: this.form.orderNo,
          question_content: this.form.demand + "\n" + this.form.description,
          content: this.form.demand,
          category: "消费者权益服务站",
        });
        const orderNo = res.data?.slug || "";
        clearFormCache();
        this.$router.replace({
          path: "/consumerRights/success",
          query: { orderNo },
        });
      } catch (e) {
        this.isSubmitting = false;
      } finally {
        loading.clear();
      }
    },
    onAbandon() {
      clearFormCache();
      this.showExitConfirm = false;
      if (this.pendingNavigation) {
        this.pendingNavigation();
        this.pendingNavigation = null;
      } else if (this.leaveCallback) {
        this.leaveCallback();
        this.leaveCallback = null;
      } else {
        this.$router.replace("/consumerRights");
      }
    },
    onContinue() {
      this.showExitConfirm = false;
      this.pendingNavigation = null;
      this.leaveCallback = null;
    },
    handleBack() {
      if (this.isSubmitting) return;
      if (this.hasFormData) {
        this.showExitConfirm = true;
        this.leaveCallback = () => {
          this.$router.replace("/consumerRights");
        };
      } else {
        this.$router.replace("/consumerRights");
      }
    },
  },
};
</script>

<style lang="less" scoped>
.consumer-rights-form {
  padding-top: 16px;
  background: #fff;
  padding-bottom: 41px;
}

.form-nav {
  display: flex;
  align-items: center;
  padding: 10px 19px;
  background: #fff;
}

.nav-back {
  width: 9px;
  height: 14px;
  cursor: pointer;
}

.nav-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.9);
  line-height: 24px;
  margin-right: 9px;
}

.form-body {
  padding: 0 16px;
}

.form-label {
  display: flex;
  align-items: center;
  margin-top: 16px;

  &:first-child {
    margin-top: 8px;
  }

  &.form-label-single {
    margin-top: 16px;
  }
}

.label-text {
  font-size: 14px;
  color: #333;
  line-height: 16px;
}

.label-required {
  font-size: 14px;
  color: #f00;
  line-height: 16px;
}

.form-input-wrap {
  margin-top: 12px;
  background: #f2f2f7;
  border-radius: 8px;
  padding: 16px 12px;
}

.form-input {
  width: 100%;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #333;
  line-height: 16px;
  outline: none;

  &::placeholder {
    color: #b9b9b9;
  }
}

.form-textarea-wrap {
  position: relative;
  margin-top: 12px;
  background: #f2f2f7;
  border-radius: 8px;
  padding: 16px;
  min-height: 129px;
  box-sizing: border-box;
}

.form-textarea {
  width: 100%;
  min-height: 80px;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #333;
  line-height: 16px;
  resize: none;
  outline: none;

  &::placeholder {
    color: #b9b9b9;
  }
}

.char-count {
  position: absolute;
  right: 16px;
  bottom: 16px;
  font-size: 14px;
  color: #999;
  line-height: 16px;
}

.submit-btn {
  display: block;
  width: calc(100% - 1px);
  margin: 38px auto 0;
  padding: 10px 0;
  border: none;
  border-radius: 20px;
  background: #4fa4d3;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  cursor: pointer;
}
</style>
